import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { errorSnackBar, successSnackBar } from "../actions/common.action";
import { saveAccountingConnectionSettings } from "../slice/accounting.slice";

export function useAccountingForm(selectedConnection, accountsList, taxRatesList, currenciesList, loadingMetadata, onSaveSuccess, itemsList = []) {
  const dispatch = useDispatch();

  const [invoiceStatus, setInvoiceStatus] = useState("DRAFT");
  const [taxType, setTaxType] = useState("GST on Income");
  const [taxExemptType, setTaxExemptType] = useState("BAS Excluded");
  const [accountCode, setAccountCode] = useState("200");
  const [retentionAccountCode, setRetentionAccountCode] = useState("");
  const [purchaseOrderStatus, setPurchaseOrderStatus] = useState("DRAFT");
  const [expenseAccountCode, setExpenseAccountCode] = useState("400");
  const [cogsAccountCode, setCogsAccountCode] = useState("400");
  const [contractorAccountCode, setContractorAccountCode] = useState("400");
  const [cogsAccountType, setCogsAccountType] = useState("EXPENSE");
  const [contractorAccountType, setContractorAccountType] = useState("EXPENSE");
  const [defaultCurrency, setDefaultCurrency] = useState("AUD");
  const [expenseTaxType, setExpenseTaxType] = useState("GST on Expenses");
  const [retentionPayableAccountCode, setRetentionPayableAccountCode] = useState("");
  const [enableRetentionMapping, setEnableRetentionMapping] = useState(false);
  const [trackingCategoryName, setTrackingCategoryName] = useState("");
  const [squashLinesInvoices, setSquashLinesInvoices] = useState(false);
  const [squashLinesBills, setSquashLinesBills] = useState(false);
  const [squashRetention, setSquashRetention] = useState(false);
  const [lineAmountTypes, setLineAmountTypes] = useState("Exclusive");
  const [defaultItemId, setDefaultItemId] = useState("");

  const [isDirty, setIsDirty] = useState(false);
  const initialValuesRef = useRef(null);

  const selectedConnectionId = selectedConnection?.id;

  // Track dirty changes
  useEffect(() => {
    if (!selectedConnectionId || !initialValuesRef.current) {
      setIsDirty(false);
      return;
    }
    const hasChanges =
      invoiceStatus !== initialValuesRef.current.invoiceStatus ||
      taxType !== initialValuesRef.current.taxType ||
      taxExemptType !== initialValuesRef.current.taxExemptType ||
      accountCode !== initialValuesRef.current.accountCode ||
      retentionAccountCode !== initialValuesRef.current.retentionAccountCode ||
      purchaseOrderStatus !== initialValuesRef.current.purchaseOrderStatus ||
      expenseAccountCode !== initialValuesRef.current.expenseAccountCode ||
      cogsAccountCode !== initialValuesRef.current.cogsAccountCode ||
      contractorAccountCode !== initialValuesRef.current.contractorAccountCode ||
      defaultCurrency !== initialValuesRef.current.defaultCurrency ||
      expenseTaxType !== initialValuesRef.current.expenseTaxType ||
      retentionPayableAccountCode !== initialValuesRef.current.retentionPayableAccountCode ||
      enableRetentionMapping !== initialValuesRef.current.enableRetentionMapping ||
      trackingCategoryName !== initialValuesRef.current.trackingCategoryName ||
      squashLinesInvoices !== initialValuesRef.current.squashLinesInvoices ||
      squashLinesBills !== initialValuesRef.current.squashLinesBills ||
      squashRetention !== initialValuesRef.current.squashRetention ||
      lineAmountTypes !== initialValuesRef.current.lineAmountTypes ||
      defaultItemId !== initialValuesRef.current.defaultItemId ||
      cogsAccountType !== initialValuesRef.current.cogsAccountType ||
      contractorAccountType !== initialValuesRef.current.contractorAccountType;

    setIsDirty(hasChanges);
  }, [
    invoiceStatus, taxType, taxExemptType, accountCode, retentionAccountCode,
    purchaseOrderStatus, expenseAccountCode, cogsAccountCode, contractorAccountCode,
    defaultCurrency, expenseTaxType, retentionPayableAccountCode, trackingCategoryName,
    squashLinesInvoices, squashLinesBills, squashRetention, lineAmountTypes,
    defaultItemId,
    cogsAccountType, contractorAccountType, selectedConnectionId
  ]);

  const selectConnection = useCallback((connection) => {
    const settings = connection.settings || {};

    // Clear initialValuesRef temporarily during settings population to prevent false warnings
    initialValuesRef.current = null;

    setInvoiceStatus(settings.invoiceStatus || "DRAFT");
    setTaxType(settings.taxType || "GST on Income");
    setTaxExemptType(settings.taxExemptType || "BAS Excluded");
    setAccountCode(settings.accountCode || "200");
    setEnableRetentionMapping(settings.enableRetentionMapping || false);
    setRetentionAccountCode(settings.retentionAccountCode || "");
    setPurchaseOrderStatus(settings.purchaseOrderStatus || "DRAFT");
    setExpenseAccountCode(settings.expenseAccountCode || "400");
    setCogsAccountCode(settings.cogsAccountCode || settings.expenseAccountCode || "400");
    setContractorAccountCode(settings.contractorAccountCode || settings.expenseAccountCode || "400");
    setCogsAccountType(settings.cogsAccountType || "EXPENSE");
    setContractorAccountType(settings.contractorAccountType || "EXPENSE");
    setDefaultCurrency((settings.defaultCurrency === undefined || settings.defaultCurrency === null) ? "AUD" : (settings.defaultCurrency === "" ? "none" : settings.defaultCurrency));
    setExpenseTaxType(settings.expenseTaxType || "GST on Expenses");
    setRetentionPayableAccountCode(settings.retentionPayableAccountCode || "");
    setTrackingCategoryName(settings.trackingCategoryName === "" ? "none" : (settings.trackingCategoryName || ""));
    const loadedSquashInvoices = settings.squashLinesInvoices !== undefined ? settings.squashLinesInvoices : (settings.squashLineItems !== undefined ? settings.squashLineItems : true);
    const loadedSquashBills = settings.squashLinesBills !== undefined ? settings.squashLinesBills : (settings.squashLineItems === true);
    setSquashLinesInvoices(loadedSquashInvoices);
    setSquashLinesBills(loadedSquashBills);
    setSquashRetention(settings.squashRetention || false);
    setLineAmountTypes(settings.lineAmountTypes || "Exclusive");
    setDefaultItemId(settings.defaultItemId || "1");

    // Setup initial loaded values
    const initialSnapshot = {
      invoiceStatus: settings.invoiceStatus || "DRAFT",
      taxType: settings.taxType || "GST on Income",
      taxExemptType: settings.taxExemptType || "BAS Excluded",
      accountCode: settings.accountCode || "200",
      enableRetentionMapping: settings.enableRetentionMapping || false,
      retentionAccountCode: settings.retentionAccountCode || "",
      purchaseOrderStatus: settings.purchaseOrderStatus || "DRAFT",
      expenseAccountCode: settings.expenseAccountCode || "400",
      cogsAccountCode: settings.cogsAccountCode || settings.expenseAccountCode || "400",
      contractorAccountCode: settings.contractorAccountCode || settings.expenseAccountCode || "400",
      cogsAccountType: settings.cogsAccountType || "EXPENSE",
      contractorAccountType: settings.contractorAccountType || "EXPENSE",
      defaultCurrency: (settings.defaultCurrency === undefined || settings.defaultCurrency === null) ? "AUD" : (settings.defaultCurrency === "" ? "none" : settings.defaultCurrency),
      expenseTaxType: settings.expenseTaxType || "GST on Expenses",
      retentionPayableAccountCode: settings.retentionPayableAccountCode || "",
      trackingCategoryName: settings.trackingCategoryName === "" ? "none" : (settings.trackingCategoryName || ""),
      squashLinesInvoices: loadedSquashInvoices,
      squashLinesBills: loadedSquashBills,
      squashRetention: settings.squashRetention || false,
      lineAmountTypes: settings.lineAmountTypes || "Exclusive",
      defaultItemId: settings.defaultItemId || "1"
    };
    initialValuesRef.current = initialSnapshot;
  }, []);

  // Track active connection ID to prevent re-initializing snapshot on form edits
  const activeConnIdRef = useRef(null);

  // Auto-initialize form snapshot when a new connection is selected
  useEffect(() => {
    if (selectedConnection && selectedConnection.id !== activeConnIdRef.current) {
      activeConnIdRef.current = selectedConnection.id;
      selectConnection(selectedConnection);
    }
  }, [selectedConnection?.id, selectedConnection, selectConnection]);

  // Reactive default settings population from loaded metadata lists
  useEffect(() => {
    if (!selectedConnectionId || loadingMetadata) return;
    const settings = selectedConnection?.settings || {};

    if (accountsList.length > 0) {
      if (!settings.accountCode && !isDirty) {
        const salesAcc = accountsList.find(a => {
          const name = (a.name || '').toLowerCase();
          const type = (a.type || '').toLowerCase();
          return name.includes('sales') || name.includes('revenue') || type.includes('revenue') || type.includes('sales');
        });
        if (salesAcc) {
          setAccountCode(salesAcc.code);
        }
      }
      if (!settings.cogsAccountCode && !isDirty) {
        const cogsAcc = accountsList.find(a => {
          const name = (a.name || '').toLowerCase();
          const type = (a.type || '').toLowerCase();
          return name.includes('cost of goods sold') || name.includes('cogs') || name.includes('cost of sales') || type.includes('cogs') || type.includes('costofgoods');
        });
        if (cogsAcc) {
          setCogsAccountCode(cogsAcc.code);
        } else {
          const fallbackCogs = accountsList.find(a => {
            const name = (a.name || '').toLowerCase();
            return name.includes('cost of sale') || name.includes('cost of goods');
          });
          if (fallbackCogs) {
            setCogsAccountCode(fallbackCogs.code);
          }
        }
      }
      if (!settings.contractorAccountCode && !isDirty) {
        let contractorAcc = accountsList.find(a => {
          const name = (a.name || '').toLowerCase();
          return name.includes('subcontractor') ||
            name.includes('sub-contractor') ||
            name.includes('sub contractor') ||
            name.includes('contractor') ||
            name.includes('contract labor') ||
            name.includes('contract labour') ||
            name.includes('sub-contract') ||
            name.includes('cost of labor') ||
            name.includes('cost of labour') ||
            name.includes('labor') ||
            name.includes('labour');
        });

        if (!contractorAcc) {
          contractorAcc = accountsList.find(a => {
            const name = (a.name || '').toLowerCase();
            return name.includes('purchases') || name.includes('direct cost');
          });
        }

        if (contractorAcc) {
          setContractorAccountCode(contractorAcc.code);
        }
      }
    }

    if (taxRatesList.length > 0) {
      setTaxType(prev => {
        if (!prev || prev === 'GST on Income' || prev === 'GST') {
          const exemptRate = taxRatesList.find(t => String(t.name || t.code).toLowerCase().includes('tax exempt') || String(t.name || t.code).toLowerCase().includes('exempt'));
          return exemptRate ? (exemptRate.code || exemptRate.id || exemptRate.name) : taxRatesList[0].code;
        }
        return prev;
      });
      setExpenseTaxType(prev => {
        if (!prev || prev === 'GST on Expenses' || prev === 'GST') {
          const exemptRate = taxRatesList.find(t => {
            const nameLower = String(t.name || t.code).toLowerCase();
            return (nameLower.includes('tax exempt') || nameLower.includes('exempt')) && !nameLower.includes('sales');
          }) || taxRatesList.find(t => String(t.name || t.code).toLowerCase().includes('tax exempt') || String(t.name || t.code).toLowerCase().includes('exempt'));
          return exemptRate ? (exemptRate.code || exemptRate.id || exemptRate.name) : taxRatesList[0].code;
        }
        return prev;
      });
    }

    if (currenciesList.length > 0) {
      setDefaultCurrency(prev => {
        const isValid = currenciesList.some(c => c.code === prev);
        if (!isValid || prev === 'none' || prev === '') {
          return currenciesList[0].code;
        }
        return prev;
      });
    }

    if (itemsList && itemsList.length > 0) {
      setDefaultItemId(prev => {
        const isValid = itemsList.some(i => i.code === prev);
        if (!isValid || prev === '' || prev === undefined) {
          const defaultItemOne = itemsList.find(i => String(i.code) === '1');
          return defaultItemOne ? defaultItemOne.code : itemsList[0].code;
        }
        return prev;
      });
    }
  }, [accountsList, taxRatesList, currenciesList, itemsList, selectedConnectionId, loadingMetadata, isDirty]);

  // Auto-update cogsAccountCode when cogsAccountType changes
  useEffect(() => {
    if (selectedConnection?.platform === 'xero' && cogsAccountType && accountsList && accountsList.length > 0) {
      const filtered = accountsList.filter(a => {
        const t = String(a.type || '').toUpperCase();
        const f = String(cogsAccountType || '').toUpperCase();
        return (f === 'DIRECTCOST' || f === 'DIRECTCOSTS')
          ? (t === 'DIRECTCOST' || t === 'DIRECTCOSTS')
          : t === f;
      });
      const isCurrentValid = filtered.some(a => a.code === cogsAccountCode);
      if (!isCurrentValid && filtered.length > 0) {
        setCogsAccountCode(filtered[0].code);
      }
    }
  }, [cogsAccountType, accountsList, selectedConnection, cogsAccountCode]);

  // Auto-update contractorAccountCode when contractorAccountType changes
  useEffect(() => {
    if (selectedConnection?.platform === 'xero' && contractorAccountType && accountsList && accountsList.length > 0) {
      const filtered = accountsList.filter(a => {
        const t = String(a.type || '').toUpperCase();
        const f = String(contractorAccountType || '').toUpperCase();
        return (f === 'DIRECTCOST' || f === 'DIRECTCOSTS')
          ? (t === 'DIRECTCOST' || t === 'DIRECTCOSTS')
          : t === f;
      });
      const isCurrentValid = filtered.some(a => a.code === contractorAccountCode);
      if (!isCurrentValid && filtered.length > 0) {
        setContractorAccountCode(filtered[0].code);
      }
    }
  }, [contractorAccountType, accountsList, selectedConnection, contractorAccountCode]);

  const saveSettings = async () => {
    if (!selectedConnectionId) return;
    const payload = {
      invoiceStatus,
      taxType,
      taxExemptType,
      accountCode,
      retentionAccountCode,
      purchaseOrderStatus,
      expenseAccountCode,
      cogsAccountCode,
      contractorAccountCode,
      cogsAccountType,
      contractorAccountType,
      defaultCurrency: defaultCurrency === "none" ? "" : defaultCurrency,
      expenseTaxType,
      retentionPayableAccountCode,
      trackingCategoryName: trackingCategoryName === "none" ? "" : trackingCategoryName,
      squashLinesInvoices,
      squashLinesBills,
      squashRetention,
      lineAmountTypes,
      defaultItemId,
      enableRetentionMapping
    };

    await dispatch(saveAccountingConnectionSettings(selectedConnectionId, payload));

    if (onSaveSuccess) {
      onSaveSuccess(payload);
    }

    // Update snapshot with the newly saved values
    initialValuesRef.current = {
      invoiceStatus,
      taxType,
      taxExemptType,
      accountCode,
      enableRetentionMapping,
      retentionAccountCode,
      purchaseOrderStatus,
      expenseAccountCode,
      cogsAccountCode,
      contractorAccountCode,
      cogsAccountType,
      contractorAccountType,
      defaultCurrency,
      expenseTaxType,
      retentionPayableAccountCode,
      trackingCategoryName,
      squashLinesInvoices,
      squashLinesBills,
      squashRetention,
      lineAmountTypes,
      defaultItemId
    };

    setIsDirty(false);
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      await saveSettings();
    } catch (err) {
      const isSyncLock = err?.response?.data?.errorCode === 'SYNC_LOCK' || err?.response?.status === 423;
      if (isSyncLock) {
        dispatch(errorSnackBar('This connection is currently syncing. Settings are read-only until the sync completes.'));
      } else {
        dispatch(errorSnackBar(err.message || 'Failed to update settings'));
      }
    }
  };

  return {
    invoiceStatus, setInvoiceStatus,
    taxType, setTaxType,
    taxExemptType, setTaxExemptType,
    accountCode, setAccountCode,
    retentionAccountCode, setRetentionAccountCode,
    purchaseOrderStatus, setPurchaseOrderStatus,
    expenseAccountCode, setExpenseAccountCode,
    cogsAccountCode, setCogsAccountCode,
    contractorAccountCode, setContractorAccountCode,
    cogsAccountType, setCogsAccountType,
    contractorAccountType, setContractorAccountType,
    defaultCurrency, setDefaultCurrency,
    expenseTaxType, setExpenseTaxType,
    retentionPayableAccountCode, setRetentionPayableAccountCode,
    enableRetentionMapping, setEnableRetentionMapping,
    trackingCategoryName, setTrackingCategoryName,
    squashLinesInvoices, setSquashLinesInvoices,
    squashLinesBills, setSquashLinesBills,
    squashRetention, setSquashRetention,
    lineAmountTypes, setLineAmountTypes,
    defaultItemId, setDefaultItemId,
    isDirty, setIsDirty,
    selectConnection,
    saveSettings,
    handleSaveSettings
  };
}
